import React, { useState, useEffect, useRef } from 'react';
import "../../styles/components/DoctorForm.scss";

import Button from '../Utilities/CustomButton';
import Fieldset from '../Form/Fieldset';
import FormInput from '../Form/FormInput';
import axios from 'axios';
import Upload from '../Documents/UploaderButton';

// *******************************
// STEPPER
// *******************************
const DoctorStepper = ({ className='', steps = 4, current = 1, onChange, ...props }) => {
  
  if (steps <= 0) {
    return null;
  }
  return (
    <div className={`doctor-stepper ${className}`}>
      <div className={`doctor-stepper__step doctor-stepper__step--1 ${current == 1 ? 'doctor-stepper__step--active':''}`} onClick={() => onChange(1)}>1</div>
      {[...Array(steps-1)].map((value, i) => (
        <React.Fragment key={i}>
          <div className={`doctor-stepper__sep`}></div>
          <div className={`doctor-stepper__step doctor-stepper__step--${i+2} ${current == (i+2) ? 'doctor-stepper__step--active':''}`} onClick={() => onChange(i+2)}>{i+2}</div>
        </React.Fragment>
      ))}
    </div>
  );
}


// *******************************
// FORM 1
// *******************************
const Form1 = ({ className='', data, updateData }) => {
  return (
    <div className={`doctor-form1 ${className}`} >
      <div className='doctor-form__row' >
        <div className='doctor-form__left'>
          <div className="fieldset-row">
            <FormInput label="Nom du patient" placeholder="Nom" type="text" value={data['patient_lastname']} onChange={(value) => updateData('patient_lastname', value)} />
            <FormInput label="Nom d'usage du patient" placeholder="Nom d'usage" type="text" value={data['patient_nickname']} onChange={(value) => updateData('patient_nickname', value)} />
          </div>
          <div className="fieldset-row">
            <FormInput label="Prénom du patient" placeholder="Prénom" type="text" value={data['patient_firstname']} onChange={(value) => updateData('patient_firstname', value)} />
            <FormInput label="Date de naissance" placeholder="XX/XX/XXXX"min="1910-01-01"  max={new Date().toISOString().split('T')[0]}  type="date" value={data['patient_birthday']} onChange={(value) => updateData('patient_birthday', value)} />
            <FormInput label="Lieu de soin" placeholder="Code postal" type="zipcode" value={data['patient_zipcode']} onChange={(value) => updateData('patient_zipcode', value)} />
          </div>
        </div>
        <div className='doctor-form__right'>
          <div className="fieldset-row">
            <FormInput label="Nom, prénom du médecin" placeholder="Nom, prénom" type="text" value={data['medecin_name']} onChange={(value) => updateData('medecin_name', value)} />
            <FormInput label="Numéro RPPS" placeholder="" type="text" value={data['medecin_rpps']} onChange={(value) => updateData('medecin_rpps', value)} />
          </div>
          <div className="fieldset-row">
            <FormInput label="Motif de la consulation" id="motif" placeholder="Motif de la consulatation" type="text" value={data['consulation_motif']} onChange={(value) => updateData('consulation_motif', value)} />
          </div>
        </div>
      </div>
    </div>
  );
}


// *******************************
// FORM 2
// *******************************
const gender = { male: 'Homme', female: 'Femme' };
const Form2 = ({ className='', data, updateData }) => {
  return (
    <div className={`doctor-form2 ${className}`}>
      <div className='doctor-form__row'>
        <div className='doctor-form__left'>
          <FormInput label="Observations médicales et diagnostic" type="textarea" value={data['medecin_observation']} onChange={(value) => updateData('medecin_observation', value)} />
        </div>
        <div className='doctor-form__right'>
          <FormInput label="Données médicales du patient" type={null} />
          <ul className="doctor-form__datas">
            {
              [
                { icon: 'gender', value: gender[data['sexe']] },
                { icon: 'cake', value: data['age'], suffix: 'Ans' },
                { icon: 'meter', value: data['taille'], suffix: 'cm' },
                { icon: 'weight', value: data['poids'], suffix: 'kg' },
                { icon: 'temp', value: data['temperature'], suffix: '°C' },
                { icon: 'glycemie', value: data['glycemie'], suffix: 'ml/l' },
                { icon: 'oxymetrie', value: data['oxymetrie'], suffix: '%' },
              ].filter(item => item.value).map(({ icon, value, suffix }) => (<li key={icon} className='doctor-data-item'>
                <span className='doctor-data-item__icon'>
                  <i className={`icon-${icon}`}></i>
                </span>
                <span className='doctor-data-item__value'>{value}</span>
                <span className='doctor-data-item__suffix'>{suffix}</span>
              </li>))
            }
          </ul>
        </div>
      </div>
    </div>
  );
}

// *******************************
// FORM 3
// *******************************
const Form3 = ({ className='', data,  updateData }) => {

  const [docs, setDocuments] = useState([]);
  const listenerAdded = useRef(false);
  const listeners = [];
  useEffect(() => {
    const loadDocs = async () => {
      const docs = await window.sdk.session().getDocuments();
      setDocuments(docs.filter(doc => doc.type && doc.type.startsWith('image')));
    };

    loadDocs();

    if (listenerAdded.current) return;

    listeners.push(window.sdk.event().on('newMessage', async (newMessage) => {
      let doc = newMessage.message.document;
      if(doc){
        if(!doc.type.startsWith('image'))
        return;
        const docUrl = '//' + window.sdk.getApiUrl() + doc.path;
        const currentRenduFiles = data['rendu_files'] || [];
        const updatedRenduFiles = [...currentRenduFiles, docUrl];
        loadDocs();
        updateData('rendu_files', updatedRenduFiles);        
      }
    }));

    listenerAdded.current = true;

    return () => {
			for (let i in listeners) {
				listeners[i]();
        listenerAdded.current = false;
			}
		}
  }, [data]);

  const getFileIcon = (file) => {
    let icon = "icon-file-link";
    switch(file.type) {
        case 'application/pdf':
            icon = "icon-file-pdf";
            break;
        case 'image/jpeg':
        case 'image/jpg':
        case 'image/png':
        case 'image/bmp':
        case 'image/gif':
            icon = "icon-file-camera";
            break;
        case 'audio/mp3':
            icon = "icon-file-audio";
            break;
        case 'text/txt':
            icon = "icon-file-doc";
            break;
        case 'video/mp4':
        case 'video/webm':
            icon = "icon-file-video";
            break;
        default : 
          icon = 'icon-file-link';
          break;
    }
    return icon;
  }


  let documents = docs.map(doc => ({
    ...doc,
    icon: getFileIcon(doc),
    label: doc.clean_name,
    value: '//' + window.sdk.getApiUrl() + doc.path
    }
  ));

  const onViewClick = (file) => {
    if(file.type.startsWith('image') || file.type.startsWith('video') || file.type.startsWith('audio'))
        window.sdk.lightboxMedia(file);
    else
        window.open('//' + window.sdk.getApiUrl() + file.path, '_blank');
  }


  const onDownloadClick = (file) => {
    fetch('//' + window.sdk.getApiUrl() + file.path).then(response => {
        response.blob().then(blob => {
            // Creating new object of PDF file
            const fileURL = window.URL.createObjectURL(blob);
            // Setting various property values
            let alink = document.createElement('a');
            alink.href = fileURL;
            alink.download = file.name;
            alink.click();
        })
    })
}  

const uploadFile = async(file) => {
  
}

  return (
    <div className={`doctor-form3 ${className}`}>
      <div className='doctor-form__row'>
        <div className='doctor-form__left'>
          <FormInput label={(<>Fichier joints <small>Veuillez cocher les fichiers à incorporer au compte rendu</small></>)} 
            type="multiselect-files" 
            value={data['rendu_files']} 
            options={documents}
            onChange={(value) => updateData('rendu_files', value)}
            onView={onViewClick}
            onDownload={onDownloadClick}
            />
        </div>
        <div className='doctor-form__right'>
        <Upload accept='image/*'>
          <div className="form-input form-input--upload  ">
              <label className="form-input__label">Téléverser</label>
              <p className="form-input__description">Vous pouvez joindre une image, une ordonnance en format pdf ou le compte rendu d’un autre dispositif médical.</p>
              <div className="form-input__file">
                <div className="input-file-custom">
                  <button className="button button--medical button--primary button--outline  input-file-custom__button">
                    <i className="icon-upload" /> Téléverser un fichier
                  </button>
                </div>
              </div>
            </div>
        </Upload>
        </div>
      </div>
    </div>
  );
}


// *******************************
// FORM 4
// *******************************
const Form4 = ({ className='', data, updateData }) => {
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);

  const displayReport = async () => {
    setIsPreviewLoading(true);
    try {
      const response = await window.sdk.session().previewCR();
      window.sdk.lightboxHtml(response.html);
    } catch (e) {
      console.error(e);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const downloadReport = async () => {
    try {
      setIsDownloadLoading(true); // Démarre le chargement

      let response = await window.sdk.session().downloadCR(); // renvoie la Response
      const blob = await response.blob(); // on récupère le blob

      const disposition = response.headers.get('content-disposition');
      let filename = 'document.pdf'; // fallback

      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      const url = window.URL.createObjectURL(blob); // ici, pas besoin de new Blob
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement :', error);
    } finally {
      setIsDownloadLoading(false); // Fin du chargement
    }
  }

  return (
    <div className={`doctor-form4 ${className}`}>
      <FormInput label="Recommandations et suivi" 
        type="textarea" 
        value={data['recommandations']} 
        onChange={(value) => updateData('recommandations', value)} />
      <div className='doctor-form__row'>
        <div className='doctor-form__left'>
          {/*<FormInput  
            type="select" 
            value={data['incidents']} 
            options={[
              { label: 'Incidents pendant la consultation', value: '' },
              { label: 'Incident 1', value: '1' },
            ]}
          onChange={(value) => updateData('incidents', value)} /> */}
        </div>
        <div className='doctor-form__right'>
          <div className="doctor-form__buttons">
            <Button outline={true} onClick={displayReport} disabled={isPreviewLoading}>
              <i className="icon-view" />Visualiser
            </Button>
            <Button onClick={downloadReport} disabled={isDownloadLoading}>
              Télécharger le compte rendu
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}




// *******************************
// MAIN
// *******************************
const DoctorForm = ({ className='', data = {}, onUpdate, display, ...props }) => {
  //console.log('aaaaaaaaaaaaaaaaaaaaa',dataForm)
  const [step, setStep] = useState(1);
  //const [data, setData] = useState(dataForm);
  const stepRef = useRef(step);
  const listenerAdded = useRef(false);
  const timeout = useRef();

  const [localData, setLocalData] = useState(data);
    
  useEffect(() => {
      setLocalData(data);
  }, [data]);

  const _setStep = async (nb) => {

    setStep(nb);
    stepRef.current = nb;
  }
  

  return (
    <div className={`doctor-form ${display ? 'doctor-form--opened' : ''} ${className}`}>
      <div className='doctor-form__inner'>
        <div className={`doctor-form__arrow doctor-form__arrow--left ${step <= 1 ? 'doctor-form__arrow--disabled' : ''}`}>
          <i className="icon-chevron-up" onClick={() => step > 1 ? _setStep(step-1) : null} />
        </div>
        <div className='doctor-form__content'>
          <div className={`doctor-form__form ${step === 1 ? 'doctor-form__form--active' : ''}`}>
            <Form1 data={localData} updateData={onUpdate} />
          </div>
          <div className={`doctor-form__form ${step === 2 ? 'doctor-form__form--active' : ''}`}>
            <Form2 data={localData} updateData={onUpdate} />
          </div>
          <div className={`doctor-form__form ${step === 3 ? 'doctor-form__form--active' : ''}`}>
            <Form3 data={localData} updateData={onUpdate} />
          </div>
          <div className={`doctor-form__form ${step === 4 ? 'doctor-form__form--active' : ''}`}>
            <Form4 data={localData} updateData={onUpdate} />
          </div>
        </div>
        <div className={`doctor-form__arrow doctor-form__arrow--right ${step >= 4 ? 'doctor-form__arrow--disabled' : ''}`}>
          <i className="icon-chevron-down" onClick={() => step < 4 ? _setStep(step+1) : null} />
        </div>
      </div>
      <DoctorStepper steps={4} current={step} className='doctor-form__steps' onChange={(step) => _setStep(step)} />
    </div>
  );
}

export default DoctorForm;